import { useState, useMemo, useEffect } from "react";
import Topbar from "../components/layout/Topbar.jsx";
import CredentialModal from "../components/ui/CredentialModal.jsx";
import PasswordGeneratorModal from "../components/ui/PasswordGeneratorModal.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import TotpWidget from "../components/ui/TotpWidget.jsx";
import {
  getPersonalCredentials,
  getCompanyCredentials,
  deleteCredential,
  getCredentialPassword,
  getPassword as adminGetPassword, // credentials COMPANY : l'admin n'a pas CREDENTIAL_COPY_COMPANY, il faut l'endpoint /admin/{id}/password (hasRole SUPER_ADMIN)
  generatePassword,
  saveTotpSecret,
  deleteTotpSecret,
  getTotpCode,
  logCredentialAccess,
  adminLogCredentialAccess,
} from "../services/credentialService.js";
import { BRAND_AWARE_TYPES, resolveBrandDomain, faviconSrc } from "../utils/brandIcons.js";

// Choisit le bon endpoint de déchiffrement selon le scope de la credential —
// PERSONAL passe par la route client (CREDENTIAL_VIEW_PERSONAL), COMPANY par
// la route admin (hasRole SUPER_ADMIN). Avant ce correctif, le scope COMPANY
// passait toujours par la route client qui exige CREDENTIAL_COPY_COMPANY —
// permission que le rôle SUPER_ADMIN ne possède pas, d'où l'échec silencieux
// du bouton œil dans l'onglet Entreprise.
const fetchPasswordFor = (cred) =>
  cred.scope === "COMPANY" ? adminGetPassword(cred.id) : getCredentialPassword(cred.id);

// Même logique de routage que fetchPasswordFor, appliquée à la journalisation
// du journal d'accès : une credential COMPANY est journalisée via la route
// admin (hasRole SUPER_ADMIN), une credential PERSONAL via la route client.
const logAccessFor = (cred, opts) =>
  (cred.scope === "COMPANY" ? adminLogCredentialAccess : logCredentialAccess)(cred.id, opts).catch(() => {});

// ─────────────────────────────────────────────
//  MÉTADONNÉES PAR TYPE — aligné sur CredentialType.java
// ─────────────────────────────────────────────

const TYPE_META = {
  WEBSITE:     { label: "Site Web",        bg: "#3B5BDB" },
  APPLICATION: { label: "Application",     bg: "#6741D9" },
  DATABASE:    { label: "Base de données", bg: "#C2255C" },
  EMAIL:       { label: "E-mail",          bg: "#2F9E44" },
  SERVER:      { label: "Serveur",         bg: "#3D6B6B" },
  SSH:         { label: "SSH",             bg: "#1A1A1A" },
  VPN:         { label: "VPN",             bg: "#0077B6" },
  WIFI:        { label: "WiFi",            bg: "#9B2AE8" },
  ROUTER:      { label: "Routeur",         bg: "#E85D04" },
  IP_PHONE:    { label: "Téléphonie IP",   bg: "#0CA678" },
  PBX:         { label: "PBX",             bg: "#F08C00" },
  SWITCH:      { label: "Switch",          bg: "#495057" },
  OTHER:       { label: "Autre",           bg: "#F76707" },
};

// ─────────────────────────────────────────────
//  MINI VIGNETTES SVG PAR TYPE
// ─────────────────────────────────────────────

const THUMB = {
  WEBSITE: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="tw"><stop offset="0%" stopColor="#3B5BDB"/><stop offset="100%" stopColor="#4DABF7"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#tw)"/>
      <circle cx="40" cy="24" r="14" fill="none" stroke="rgba(255,255,255,.9)" strokeWidth="1.6"/>
      <ellipse cx="40" cy="24" rx="6" ry="14" fill="none" stroke="rgba(255,255,255,.65)" strokeWidth="1.2"/>
      <line x1="26" y1="24" x2="54" y2="24" stroke="rgba(255,255,255,.65)" strokeWidth="1.2"/>
    </svg>
  ),
  APPLICATION: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="ta"><stop offset="0%" stopColor="#6741D9"/><stop offset="100%" stopColor="#9775FA"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#ta)"/>
      <circle cx="40" cy="24" r="9" fill="rgba(255,255,255,.85)"/>
      <line x1="40" y1="32" x2="40" y2="20" stroke="#6741D9" strokeWidth="2" strokeLinecap="round"/>
      <polyline points="35,24 40,19 45,24" stroke="#6741D9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  ),
  DATABASE: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="td"><stop offset="0%" stopColor="#C2255C"/><stop offset="100%" stopColor="#F06595"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#td)"/>
      <ellipse cx="40" cy="14" rx="16" ry="5" fill="rgba(255,255,255,.85)"/>
      <rect x="24" y="14" width="32" height="9" fill="rgba(255,255,255,.72)"/>
      <ellipse cx="40" cy="23" rx="16" ry="5" fill="rgba(255,255,255,.85)"/>
      <rect x="24" y="23" width="32" height="9" fill="rgba(255,255,255,.6)"/>
      <ellipse cx="40" cy="32" rx="16" ry="5" fill="rgba(255,255,255,.85)"/>
    </svg>
  ),
  EMAIL: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="te"><stop offset="0%" stopColor="#2F9E44"/><stop offset="100%" stopColor="#69DB7C"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#te)"/>
      <rect x="16" y="13" width="48" height="30" rx="3" fill="rgba(255,255,255,.85)"/>
      <polyline points="16,13 40,30 64,13" fill="none" stroke="rgba(47,158,68,.8)" strokeWidth="1.8"/>
    </svg>
  ),
  SERVER: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="ts"><stop offset="0%" stopColor="#3D6B6B"/><stop offset="100%" stopColor="#63B3B3"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#ts)"/>
      <rect x="26" y="7" width="28" height="36" rx="3" fill="rgba(255,255,255,.15)" stroke="rgba(255,255,255,.55)" strokeWidth="1"/>
      {[11,19,27,35].map((y, i) => <g key={i}><rect x="28" y={y} width="24" height="5" rx="1.5" fill="rgba(255,255,255,.18)"/><circle cx="48" cy={y+2.5} r="1.8" fill={i<2?"#69DB7C":"#FFD43B"}/></g>)}
    </svg>
  ),
  SSH: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}>
      <rect width="80" height="48" fill="#1A1A1A"/>
      <circle cx="17" cy="13" r="2.5" fill="#FF5F57"/><circle cx="24" cy="13" r="2.5" fill="#FFBD2E"/><circle cx="31" cy="13" r="2.5" fill="#28C840"/>
      <text x="14" y="28" fontFamily="monospace" fontSize="5.5" fill="#58A6FF">$</text>
      <text x="20" y="28" fontFamily="monospace" fontSize="5.5" fill="#7EE787">ssh admin@host</text>
    </svg>
  ),
  VPN: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="tvpn"><stop offset="0%" stopColor="#0077B6"/><stop offset="100%" stopColor="#00B4D8"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#tvpn)"/>
      <rect x="26" y="14" width="28" height="20" rx="4" fill="rgba(255,255,255,.2)" stroke="rgba(255,255,255,.6)" strokeWidth="1.5"/>
      <path d="M34 24 L46 24 M40 18 L40 30" stroke="rgba(255,255,255,.9)" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  WIFI: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="tw2"><stop offset="0%" stopColor="#9B2AE8"/><stop offset="100%" stopColor="#DA77F2"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#tw2)"/>
      <circle cx="40" cy="40" r="3.5" fill="white"/>
      <path d="M30,33 Q40,25 50,33" fill="none" stroke="rgba(255,255,255,.9)" strokeWidth="3" strokeLinecap="round"/>
      <path d="M22,26 Q40,13 58,26" fill="none" stroke="rgba(255,255,255,.65)" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  ),
  ROUTER: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="tr"><stop offset="0%" stopColor="#E85D04"/><stop offset="100%" stopColor="#F48C06"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#tr)"/>
      <rect x="20" y="22" width="40" height="10" rx="3" fill="rgba(255,255,255,.85)"/>
      {[28,34,40,46].map(x => <circle key={x} cx={x} cy="27" r="2" fill="#E85D04"/>)}
      <line x1="30" y1="22" x2="25" y2="14" stroke="rgba(255,255,255,.8)" strokeWidth="1.5"/>
      <line x1="50" y1="22" x2="55" y2="14" stroke="rgba(255,255,255,.8)" strokeWidth="1.5"/>
    </svg>
  ),
  IP_PHONE: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="tip"><stop offset="0%" stopColor="#0CA678"/><stop offset="100%" stopColor="#63E6BE"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#tip)"/>
      <rect x="23" y="8" width="34" height="32" rx="4" fill="rgba(255,255,255,.9)"/>
      <rect x="27" y="13" width="26" height="8" rx="2" fill="#0CA678"/>
      {[0,1].map(r => (
        <g key={r}>
          {[0,1,2,3].map(c => <circle key={c} cx={30 + c*7} cy={27 + r*6} r="1.4" fill="#0CA678"/>)}
        </g>
      ))}
    </svg>
  ),
  PBX: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="tpbx"><stop offset="0%" stopColor="#F08C00"/><stop offset="100%" stopColor="#FFC078"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#tpbx)"/>
      <rect x="14" y="15" width="52" height="18" rx="3" fill="rgba(255,255,255,.88)"/>
      {[0,1,2,3,4].map(i => <rect key={i} x={19 + i*9} y="19" width="5" height="9" rx="1" fill="#F08C00"/>)}
    </svg>
  ),
  SWITCH: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}><defs><linearGradient id="tsw"><stop offset="0%" stopColor="#495057"/><stop offset="100%" stopColor="#868E96"/></linearGradient></defs>
      <rect width="80" height="48" fill="url(#tsw)"/>
      <rect x="12" y="19" width="56" height="12" rx="2" fill="rgba(255,255,255,.15)" stroke="rgba(255,255,255,.55)" strokeWidth="1"/>
      {Array.from({length:8}).map((_,i) => <circle key={i} cx={17 + i*7} cy="25" r="1.3" fill={i%3===0 ? "#69DB7C":"rgba(255,255,255,.5)"}/>)}
    </svg>
  ),
  OTHER: (w, h) => (
    <svg viewBox="0 0 80 48" width={w} height={h}>
      <rect width="80" height="48" fill="#f1f3f5"/>
      <circle cx="40" cy="24" r="12" fill="#F76707"/>
      <rect x="38" y="17" width="4" height="14" rx="2" fill="white"/>
      <rect x="33" y="22" width="14" height="4" rx="2" fill="white"/>
    </svg>
  ),
};

// ─────────────────────────────────────────────
//  MODALE DE VISUALISATION D'UNE CREDENTIAL
//
//  Ouverte quand l'utilisateur clique sur une ligne.
//  Affiche tous les détails + bouton œil pour révéler le mot de passe en clair.
//
//  Le mot de passe n'est jamais stocké dans la BD en clair.
//  On appelle GET /api/credentials/{id}/password qui déchiffre côté serveur.
//
//  Props :
//   - cred        : la credential à afficher
//   - isReadOnly  : true pour les COMPANY (masque Modifier/Supprimer)
//   - canCopy     : true si l'utilisateur peut copier le mot de passe
//   - onClose     : fermer la modale
//   - onEdit      : ouvrir la modale d'édition
//   - onDelete    : supprimer
// ─────────────────────────────────────────────

function CredentialViewModal({ cred, isReadOnly, canCopy, onClose, onEdit, onDelete, onUpdate }) {
  const type = (cred.type || "OTHER").toUpperCase();
  const meta = TYPE_META[type] || TYPE_META.OTHER;

  // Mot de passe : null = pas encore chargé, string = chargé
  const [password,       setPassword]       = useState(null);
  const [showPassword,   setShowPassword]   = useState(false);
  const [pwdLoading,     setPwdLoading]     = useState(false);
  const [pwdError,       setPwdError]       = useState("");
  const [copied,         setCopied]         = useState(false);
  const [deleteConfirm,  setDeleteConfirm]  = useState(false);
  const [menuOpen,       setMenuOpen]       = useState(false);
  const [logoFailed,     setLogoFailed]     = useState(false);
  // Révélation individuelle des champs personnalisés de type "Mot de passe"
  const [revealedCF,     setRevealedCF]     = useState({});

  // Vrai logo (favicon) de la marque détectée depuis le nom/l'URL, à la
  // place de l'icône générique — uniquement pour les types pertinents.
  const brandDomain = BRAND_AWARE_TYPES.has(type) && !logoFailed
    ? resolveBrandDomain(cred.name, cred.url, cred.host)
    : null;

  // ── Charge le mot de passe déchiffré depuis le backend ──
  const loadPassword = async () => {
    if (password !== null) {
      // Déjà chargé → toggle affichage/masquage
      setShowPassword(v => !v);
      return;
    }
    setPwdLoading(true);
    setPwdError("");
    try {
      const res = await fetchPasswordFor(cred);
      setPassword(res.data);      // stocke en state local (jamais en localStorage)
      setShowPassword(true);      // affiche immédiatement
    } catch (err) {
      setPwdError("Impossible de récupérer le mot de passe");
    } finally {
      setPwdLoading(false);
    }
  };

  // ── Copie le mot de passe dans le presse-papier ──
  const handleCopy = async () => {
    try {
      let pwd = password;
      if (!pwd) {
        // Pas encore chargé → on le charge d'abord
        const res = await fetchPasswordFor(cred);
        pwd = res.data;
        setPassword(pwd);
      }
      await navigator.clipboard.writeText(pwd);
      logAccessFor(cred, { targetType: "PASSWORD", action: "COPY" });
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setPwdError("Impossible de copier");
    }
  };

  // La modale ne se ferme plus au clic sur l'overlay — uniquement via le
  // bouton fermer explicite, pour éviter une fermeture accidentelle pendant
  // la consultation (ex : clic à côté en copiant plusieurs champs).
  return (
    <div className="vw-overlay">
      <div className="vw-modal">

        {/* Bouton fermer */}
        <button className="vw-close" onClick={onClose}>
          <i className="bi bi-x-lg"/>
        </button>

        {/* Bandeau coloré selon le type — vrai logo si la marque est détectée */}
        <div className="vw-banner" style={{ background: `linear-gradient(135deg, ${meta.bg}cc, ${meta.bg}88)` }}>
          {brandDomain ? (
            <div style={{ width: 56, height: 56, borderRadius: 14, background: "#fff",
                          boxShadow: "0 4px 14px rgba(0,0,0,.2)", display: "flex",
                          alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <img
                key={brandDomain}
                src={faviconSrc(brandDomain, 128)}
                alt=""
                onError={() => setLogoFailed(true)}
                style={{ width: 32, height: 32, objectFit: "contain" }}
              />
            </div>
          ) : (
            <i className={`bi ${TYPE_ICON[type] || "bi-key"}`} style={{ fontSize: 40, color: "rgba(255,255,255,.9)" }}/>
          )}
          <div className="vw-banner-text">
            <span className="vw-banner-name">{cred.name}</span>
            <span className="vw-banner-type">{meta.label}</span>
          </div>
        </div>

        {/* Corps */}
        <div className="vw-body">

          {/* Menu burger — Modifier / Copier / Supprimer (masqué si lecture seule) */}
          {!isReadOnly && (
            <div className="vw-action-row">
              <div className="cred-menu-wrap">
                <button className="vw-btn-edit" onClick={() => setMenuOpen(o => !o)}>
                  <i className="bi bi-list me-1" style={{ fontSize: 16 }}/>Actions
                </button>
                {menuOpen && (
                  <div className="cred-dropdown" style={{ left: 0, right: "auto" }} onMouseLeave={() => setMenuOpen(false)}>
                    <button className="cred-dd-item" onClick={() => { setMenuOpen(false); onClose(); onEdit(cred); }}>
                      <i className="bi bi-pencil me-2"/>Modifier
                    </button>
                    {cred.username && (
                      <button className="cred-dd-item" onClick={() => { navigator.clipboard.writeText(cred.username); setMenuOpen(false); }}>
                        <i className="bi bi-person me-2"/>Copier l'identifiant
                      </button>
                    )}
                    <button className="cred-dd-item" onClick={() => { handleCopy(); setMenuOpen(false); }}>
                      <i className="bi bi-key me-2"/>Copier le mot de passe
                    </button>
                    {cred.hasTotpSecret && (
                      <button className="cred-dd-item" onClick={async () => {
                        try {
                          const res = await getTotpCode(cred.id);
                          const code = res?.data?.code || res?.code;
                          if (code) {
                            await navigator.clipboard.writeText(code);
                            logAccessFor(cred, { targetType: "TOTP", action: "COPY" });
                          }
                        } catch (e) { alert(e.message); }
                        setMenuOpen(false);
                      }}>
                        <i className="bi bi-shield-lock me-2"/>Copier le code 2FA
                      </button>
                    )}
                    {cred.url && (
                      <button className="cred-dd-item" onClick={() => { navigator.clipboard.writeText(cred.url); setMenuOpen(false); }}>
                        <i className="bi bi-link-45deg me-2"/>Copier l'URL
                      </button>
                    )}
                    {cred.url && (
                      <a href={/^https?:\/\//i.test(cred.url) ? cred.url : `https://${cred.url}`}
                        target="_blank" rel="noopener noreferrer"
                        className="cred-dd-item"
                        style={{ textDecoration: "none", display: "flex", alignItems: "center" }}
                        onClick={() => setMenuOpen(false)}>
                        <i className="bi bi-box-arrow-up-right me-2"/>Ouvrir le site
                      </a>
                    )}
                    <div className="cred-dd-sep"/>
                    {!deleteConfirm ? (
                      <button className="cred-dd-item danger" onClick={() => setDeleteConfirm(true)}>
                        <i className="bi bi-trash me-2"/>Supprimer
                      </button>
                    ) : (
                      <div className="vw-delete-confirm" style={{ padding: "8px 14px" }}>
                        <span>Confirmer ?</span>
                        <button className="vw-confirm-yes" onClick={() => { setMenuOpen(false); onClose(); onDelete(cred.id); }}>Oui</button>
                        <button className="vw-confirm-no"  onClick={() => setDeleteConfirm(false)}>Non</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Champs */}
          <div className="vw-fields">

            {/* Nom d'utilisateur */}
            {cred.username && (
              <div className="vw-field">
                <span className="vw-field-label"><i className="bi bi-person me-1"/>Nom d'utilisateur</span>
                <div className="vw-field-value-row">
                  <span className="vw-field-value">{cred.username}</span>
                  <CopyBtn value={cred.username}/>
                </div>
              </div>
            )}

            {/* ── MOT DE PASSE ── point central de ce composant */}
            <div className="vw-field">
              <span className="vw-field-label"><i className="bi bi-key me-1"/>Mot de passe</span>
              <div className="vw-field-value-row">

                {/* Valeur : points si masqué, texte clair si révélé */}
                <span className="vw-field-value" style={{ fontFamily: showPassword ? "monospace" : "inherit", letterSpacing: showPassword ? ".05em" : "normal" }}>
                  {showPassword && password ? password : "••••••••"}
                </span>

                {/* Bouton œil — charge ET affiche le mot de passe en clair */}
                <button
                  className="vw-icon-btn"
                  onClick={loadPassword}
                  title={showPassword ? "Masquer" : "Révéler le mot de passe"}
                  disabled={pwdLoading}
                >
                  {pwdLoading
                    ? <i className="bi bi-arrow-repeat vw-spin"/>
                    : <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}/>
                  }
                </button>

                {/* Bouton copier — disponible selon permission */}
                {(canCopy || !isReadOnly) && (
                  <button className="vw-icon-btn" onClick={handleCopy} title="Copier le mot de passe">
                    <i className={`bi ${copied ? "bi-check2" : "bi-copy"}`}
                       style={{ color: copied ? "#69DB7C" : undefined }}/>
                  </button>
                )}
              </div>

              {/* Message d'erreur si le chargement échoue */}
              {pwdError && (
                <span style={{ fontSize: 11, color: "var(--danger)", marginTop: 4, display: "block" }}>
                  <i className="bi bi-exclamation-triangle me-1"/>{pwdError}
                </span>
              )}
            </div>

            {/* URL */}
            {cred.url && (
              <div className="vw-field">
                <span className="vw-field-label"><i className="bi bi-link-45deg me-1"/>URL</span>
                <div className="vw-field-value-row">
                  <a href={/^https?:\/\//i.test(cred.url) ? cred.url : `https://${cred.url}`} target="_blank" rel="noopener noreferrer" className="vw-field-link">
                    {cred.url}<i className="bi bi-box-arrow-up-right ms-1" style={{ fontSize: 10 }}/>
                  </a>
                  <CopyBtn value={cred.url}/>
                </div>
              </div>
            )}

            {/* Host / Port */}
            {cred.host && (
              <div className="vw-field">
                <span className="vw-field-label"><i className="bi bi-hdd-network me-1"/>Adresse / Hostname</span>
                <div className="vw-field-value-row">
                  <span className="vw-field-value">{cred.host}{cred.port ? `:${cred.port}` : ""}</span>
                  <CopyBtn value={cred.host + (cred.port ? `:${cred.port}` : "")}/>
                </div>
              </div>
            )}

            {/* Notes */}
            {cred.notes && (
              <div className="vw-field">
                <span className="vw-field-label"><i className="bi bi-sticky me-1"/>Notes</span>
                <span className="vw-field-value vw-notes">{cred.notes}</span>
              </div>
            )}

            {/* Champs personnalisés — masqués avec œil si de type "Mot de passe" */}
            {(() => {
              let customFields = [];
              try { customFields = cred.customFields ? JSON.parse(cred.customFields) : []; } catch {}
              return customFields.map((cf, i) => {
                const isPwd    = cf.type === "PASSWORD";
                const revealed = !!revealedCF[i];
                return (
                  <div className="vw-field" key={i}>
                    <span className="vw-field-label"><i className="bi bi-input-cursor-text me-1"/>{cf.name}</span>
                    <div className="vw-field-value-row">
                      <span className="vw-field-value" style={{ fontFamily: isPwd && revealed ? "monospace" : "inherit" }}>
                        {isPwd && !revealed ? "••••••••" : cf.value}
                      </span>
                      {isPwd && (
                        <button className="vw-icon-btn" onClick={() => setRevealedCF(p => {
                          const next = !p[i];
                          if (next) logAccessFor(cred, { targetType: "CUSTOM_FIELD", action: "VIEW", fieldName: cf.name });
                          return { ...p, [i]: next };
                        })} title={revealed ? "Masquer" : "Révéler"}>
                          <i className={`bi ${revealed ? "bi-eye-slash" : "bi-eye"}`}/>
                        </button>
                      )}
                      <CopyBtn value={cf.value} onCopy={isPwd ? () => logAccessFor(cred, { targetType: "CUSTOM_FIELD", action: "COPY", fieldName: cf.name }) : null}/>
                    </div>
                  </div>
                );
              });
            })()}

            {/* ── 2FA / TOTP ── lecture seule côté client
                L'admin configure le 2FA, le client consulte le code uniquement */}
            <TotpWidget
              credentialId={cred.id}
              hasTotpSecret={!!cred.hasTotpSecret}
              onUpdate={onUpdate}
              readOnly={cred.scope === "COMPANY"}
              admin={cred.scope === "COMPANY"}
            />

            {/* Dates */}
            {(cred.createdAt || cred.updatedAt) && (
              <div className="vw-dates">
                {cred.createdAt && <span><i className="bi bi-calendar3 me-1"/>Créé le {fmtDate(cred.createdAt)}</span>}
                {cred.updatedAt && cred.updatedAt !== cred.createdAt && (
                  <span><i className="bi bi-pencil-square me-1"/>Modifié le {fmtDate(cred.updatedAt)}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Icônes par type pour la bannière
const TYPE_ICON = {
  WEBSITE: "bi-globe2", APPLICATION: "bi-app", DATABASE: "bi-database",
  EMAIL: "bi-envelope", SERVER: "bi-server", SSH: "bi-terminal",
  VPN: "bi-shield-lock", WIFI: "bi-wifi", ROUTER: "bi-router", OTHER: "bi-key",
  IP_PHONE: "bi-telephone-fill", PBX: "bi-diagram-3-fill", SWITCH: "bi-hdd-network-fill",
};

// Petit bouton copier réutilisable
function CopyBtn({ value, onCopy }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    onCopy?.();
    setDone(true);
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <button className="vw-icon-btn" onClick={copy} title="Copier">
      <i className={`bi ${done ? "bi-check2" : "bi-copy"}`} style={{ color: done ? "#69DB7C" : undefined }}/>
    </button>
  );
}

// Formate une date ISO en français
function fmtDate(iso) {
  return new Date(iso).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** Miniature de ligne — vrai logo (favicon) si une marque est détectée
 *  depuis le nom/l'URL, sinon l'illustration THUMB générique par type. */
function RowThumb({ cred }) {
  const type = (cred.type || "OTHER").toUpperCase();
  const [failedDomain, setFailedDomain] = useState(null);
  const eligible = BRAND_AWARE_TYPES.has(type);
  const domain = eligible ? resolveBrandDomain(cred.name, cred.url, cred.host) : null;
  const ThumbFn = THUMB[type] || THUMB.OTHER;

  if (!domain || domain === failedDomain) return ThumbFn(56, 34);

  return (
    <div style={{ width: 56, height: 34, background: "#fff", display: "flex",
                  alignItems: "center", justifyContent: "center" }}>
      <img
        key={domain}
        src={faviconSrc(domain, 128)}
        alt=""
        onError={() => setFailedDomain(domain)}
        style={{ width: 30, height: 30, objectFit: "contain" }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────
//  COMPOSANT — UNE LIGNE DE LA LISTE
//
//  Props :
//   - cred      : objet credential
//   - onView    : ouvre la modale de visualisation
//   - onCopy    : callback pour copier le mot de passe
//   - onEdit    : callback pour ouvrir la modale d'édition (null si non autorisé)
//   - onDelete  : callback pour supprimer (null si non autorisé)
//
//  Les boutons modifier et supprimer sont masqués si les callbacks sont null,
//  ce qui correspond aux credentials COMPANY (lecture seule).
// ─────────────────────────────────────────────

function CredRow({ cred, onView, onCopy, onEdit, onDelete }) {
  const type = (cred.type || "OTHER").toUpperCase();
  const meta = TYPE_META[type] || TYPE_META.OTHER;
  const [menuOpen, setMenuOpen] = useState(false);

  // Modifier/Supprimer ne sont disponibles que si les deux callbacks sont fournis
  // (credentials PERSONAL). Le menu burger, lui, reste toujours visible — pour les
  // credentials COMPANY (lecture seule) il ne propose que les actions de consultation.
  const canEdit    = !!onEdit && !!onDelete;
  const isReadOnly = !canEdit;

  return (
    // Clic sur la ligne → ouvre la modale de visualisation
    <div className="cred-row" onClick={() => onView(cred)}>
      {/* Vignette */}
      <div className="cred-thumb"><RowThumb cred={cred}/></div>

      {/* Nom + login */}
      <div className="cred-info">
        <span className="cred-name">{cred.name}</span>
        {cred.username && <span className="cred-login">{cred.username}</span>}
        {cred.host     && <span className="cred-login">{cred.host}{cred.port ? `:${cred.port}` : ""}</span>}
      </div>

      {/* Actions — juste après le nom — stopPropagation pour ne pas ouvrir la modale */}
      <div className="cred-actions" onClick={e => e.stopPropagation()}>
        {/* Copier le mot de passe */}
        {onCopy && (
          <button className="cred-action-btn" title="Copier le mot de passe" onClick={() => onCopy(cred)}>
            <i className="bi bi-copy"/>
          </button>
        )}

        {/* Menu déroulant — toujours visible, adapté selon les droits */}
        <div className="cred-menu-wrap">
          <button className="cred-action-btn" onClick={() => setMenuOpen(o => !o)}>
            <i className="bi bi-list" style={{ fontSize:18 }}/>
          </button>
          {menuOpen && (
            <div className="cred-dropdown" onMouseLeave={() => setMenuOpen(false)}>
              <button className="cred-dd-item" onClick={() => { onView(cred); setMenuOpen(false); }}>
                <i className="bi bi-eye me-2"/>Voir les détails
              </button>
              {canEdit && (
                <button className="cred-dd-item" onClick={() => { onEdit(cred); setMenuOpen(false); }}>
                  <i className="bi bi-pencil me-2"/>Modifier
                </button>
              )}
              {cred.username && (
                <button className="cred-dd-item" onClick={() => { navigator.clipboard.writeText(cred.username); setMenuOpen(false); }}>
                  <i className="bi bi-person me-2"/>Copier l'identifiant
                </button>
              )}
              {onCopy && (
                <button className="cred-dd-item" onClick={() => { onCopy(cred); setMenuOpen(false); }}>
                  <i className="bi bi-key me-2"/>Copier le mot de passe
                </button>
              )}
              {cred.hasTotpSecret && (
                <button className="cred-dd-item" onClick={async () => {
                  try {
                    const res = await getTotpCode(cred.id);
                    const code = res?.data?.code || res?.code;
                    if (code) {
                      await navigator.clipboard.writeText(code);
                      logAccessFor(cred, { targetType: "TOTP", action: "COPY" });
                    }
                  } catch(e) { alert(e.message); }
                  setMenuOpen(false);
                }}>
                  <i className="bi bi-shield-lock me-2"/>Copier le code 2FA
                </button>
              )}
              {cred.url && (
                <button className="cred-dd-item" onClick={() => { navigator.clipboard.writeText(cred.url); setMenuOpen(false); }}>
                  <i className="bi bi-link-45deg me-2"/>Copier l'URL
                </button>
              )}
              {cred.url && (
                <a href={/^https?:\/\//i.test(cred.url) ? cred.url : `https://${cred.url}`}
                  target="_blank" rel="noopener noreferrer"
                  className="cred-dd-item"
                  style={{ textDecoration:"none", display:"flex", alignItems:"center" }}
                  onClick={() => setMenuOpen(false)}>
                  <i className="bi bi-box-arrow-up-right me-2"/>Ouvrir le site
                </a>
              )}
              {canEdit && (
                <>
                  <div className="cred-dd-sep"/>
                  <button className="cred-dd-item danger" onClick={() => { onDelete(cred.id); setMenuOpen(false); }}>
                    <i className="bi bi-trash me-2"/>Supprimer
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Badge type + 2FA */}
      <div className="cred-badges">
        <span className="cred-type-badge" style={{ background: meta.bg + "22", color: meta.bg, borderColor: meta.bg + "44" }}>
          {meta.label}
        </span>
        {cred.hasTotpSecret && (
          <span style={{ fontSize:11, fontWeight:600, color:"#10B981", background:"rgba(16,185,129,.1)", padding:"2px 7px", borderRadius:20 }}>
            <i className="bi bi-shield-check me-1"/>2FA
          </span>
        )}
        {isReadOnly && (
          <span className="cred-readonly-badge">
            <i className="bi bi-lock-fill me-1"/>Lecture seule
          </span>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  FILTRES PAR TYPE (communs aux deux onglets)
// ─────────────────────────────────────────────

const FILTERS = [
  { id: "ALL",         label: "Tous",             icon: "bi-grid-3x3-gap" },
  { id: "WEBSITE",     label: "Sites Web",        icon: "bi-globe2" },
  { id: "SERVER",      label: "Serveurs",         icon: "bi-server" },
  { id: "SSH",         label: "SSH",              icon: "bi-terminal" },
  { id: "DATABASE",    label: "Bases de données", icon: "bi-database" },
  { id: "EMAIL",       label: "E-mails",          icon: "bi-envelope" },
  { id: "VPN",         label: "VPN",              icon: "bi-shield-lock" },
  { id: "WIFI",        label: "WiFi",             icon: "bi-wifi" },
  { id: "IP_PHONE",    label: "Téléphonie IP",    icon: "bi-telephone-fill" },
  { id: "PBX",         label: "PBX",              icon: "bi-diagram-3-fill" },
  { id: "SWITCH",      label: "Switch",           icon: "bi-hdd-network-fill" },
  { id: "APPLICATION", label: "Applications",     icon: "bi-app" },
  { id: "OTHER",       label: "Autre",            icon: "bi-key" },
];

// ─────────────────────────────────────────────
//  LISTE FILTRÉE — réutilisée par les deux onglets
//
//  Props :
//   - credentials : tableau de credentials à afficher
//   - onCopy      : callback copie
//   - onEdit      : callback édition (null pour COMPANY)
//   - onDelete    : callback suppression (null pour COMPANY)
//   - onReload    : callback rechargement après action
// ─────────────────────────────────────────────

function CredentialList({ credentials, onCopy, onEdit, onDelete, canCopy, onReload }) {
  const [search,       setSearch]       = useState("");
  const [activeFilter, setActiveFilter] = useState("ALL");
  const [copied,       setCopied]       = useState(null);

  // Credential actuellement affichée dans la modale de visualisation
  const [viewCred, setViewCred] = useState(null);

  const isReadOnly = !onEdit || !onDelete;

  // Copie rapide depuis la liste (sans ouvrir la modale)
  const handleCopy = async (cred) => {
    try {
      await onCopy(cred);
      setCopied(cred.id);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      alert("Impossible de copier : " + err.message);
    }
  };

  // Filtrage par type et recherche texte
  const filtered = useMemo(() => {
    return credentials
      .filter(c => activeFilter === "ALL" || (c.type || "").toUpperCase() === activeFilter)
      .filter(c =>
        (c.name     || "").toLowerCase().includes(search.toLowerCase()) ||
        (c.username || "").toLowerCase().includes(search.toLowerCase()) ||
        (c.host     || "").toLowerCase().includes(search.toLowerCase())
      )
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [credentials, activeFilter, search]);

  // Groupement alphabétique
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(c => {
      const letter = (c.name?.[0] || "#").toUpperCase();
      if (!map[letter]) map[letter] = [];
      map[letter].push(c);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <>
      {/* Barre de filtres + recherche */}
      <div className="pwd-toolbar anim-fade-up">
        <div className="pwd-filters">
          {FILTERS.map(f => (
            <button
              key={f.id}
              className={`pwd-filter-btn${activeFilter === f.id ? " active" : ""}`}
              onClick={() => setActiveFilter(f.id)}
            >
              <i className={`bi ${f.icon}`}/><span>{f.label}</span>
            </button>
          ))}
        </div>
        <div className="pwd-right">
          <span className="pwd-count">{filtered.length} identifiant{filtered.length > 1 ? "s" : ""}</span>
          <div className="pwd-search-wrap">
            <i className="bi bi-search pwd-search-icon"/>
            <input
              className="pwd-search"
              placeholder="Rechercher…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="pwd-search-clear" onClick={() => setSearch("")}>
                <i className="bi bi-x"/>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Liste groupée par lettre */}
      <div className="itm-card anim-fade-up" style={{ animationDelay: ".1s", padding: 0 }}>
        {filtered.length === 0 && (
          <div className="pwd-empty">
            <i className="bi bi-shield-lock"/>
            <p>Aucun identifiant trouvé</p>
            <small>Modifiez votre recherche ou ajoutez un nouvel identifiant</small>
          </div>
        )}

        {grouped.map(([letter, items]) => (
          <div key={letter}>
            <div className="pwd-letter-sep">{letter}</div>
            {items.map((cred, idx) => (
              <div
                key={cred.id}
                style={{ borderBottom: idx < items.length - 1 ? "1px solid var(--border)" : "none" }}
              >
                {/* Toast de confirmation de copie */}
                {copied === cred.id && (
                  <div className="pwd-copy-toast">
                    <i className="bi bi-check2 me-1"/>Mot de passe copié !
                  </div>
                )}
                <CredRow
                  cred={cred}
                  onView={setViewCred}
                  onCopy={onCopy ? handleCopy : null}
                  // Passer null masque les boutons modifier/supprimer (credentials COMPANY)
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Modale de visualisation — montée ici pour rester dans le scope */}
      {viewCred && (
        <CredentialViewModal
          cred={viewCred}
          isReadOnly={isReadOnly}
          canCopy={canCopy}
          onClose={() => setViewCred(null)}
          onEdit={(c) => { setViewCred(null); onEdit?.(c); }}
          onDelete={(id) => { setViewCred(null); onDelete?.(id); }}
          onUpdate={() => { setViewCred(null); onReload?.(); }}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────
//  PAGE PRINCIPALE
// ─────────────────────────────────────────────

export default function MyPasswordsPage() {

  // ── Permissions — chargées une fois par session dans AuthContext ──────────
  // Admin : accès total — même comportement que le portail client
  const canViewPersonal = true;
  const canEditPersonal = true;
  const canViewCompany  = true;
  const canCopyCompany  = true;

  // Onglet par défaut : "personal" si autorisé, sinon "company"
  const [activeTab,     setActiveTab]     = useState("personal");
  const [personalCreds, setPersonalCreds] = useState([]);
  const [companyCreds,  setCompanyCreds]  = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState("");
  const [showModal,     setShowModal]     = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);
  const [editCred,      setEditCred]      = useState(null);

  useEffect(() => { loadAll(); }, []);

  // Charge uniquement les listes auxquelles l'utilisateur a accès
  const loadAll = async () => {
    setLoading(true);
    setError("");
    try {
      const calls = [];
      if (canViewPersonal) calls.push(getPersonalCredentials());
      if (canViewCompany)  calls.push(getCompanyCredentials());

      const results = await Promise.all(calls);

      let idx = 0;
      if (canViewPersonal) { setPersonalCreds(results[idx]?.data || []); idx++; }
      if (canViewCompany)  { setCompanyCreds(results[idx]?.data  || []); }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Copie le mot de passe dans le presse-papier (déchiffrement côté backend)
  const handleCopyPassword = async (cred) => {
    const res = await fetchPasswordFor(cred);
    await navigator.clipboard.writeText(res.data);
    logAccessFor(cred, { targetType: "PASSWORD", action: "COPY" });
  };

  // Suppression — uniquement pour les credentials PERSONAL
  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette credential ?")) return;
    try {
      await deleteCredential(id);
      setPersonalCreds(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  const handleSaved = () => {
    setShowModal(false);
    setEditCred(null);
    loadAll();
  };

  // ── Cas : aucune permission sur les mots de passe ────────────────────────
  if (!canViewPersonal && !canViewCompany) {
    return (
      <>
        <Topbar title="Coffre-fort" subtitle="de mots de passe"/>
        <div className="page-body">
          <div className="pwd-empty">
            <i className="bi bi-shield-lock" style={{ opacity: .3 }}/>
            <p>Accès non autorisé</p>
            <small>Vous n'avez pas les permissions nécessaires pour accéder aux mots de passe.</small>
          </div>
        </div>
        <style>{CSS}</style>
      </>
    );
  }

  return (
    <>
      <Topbar title="Coffre-fort" subtitle="de mots de passe"/>
      <div className="page-body">

        {/* ── En-tête : onglets + bouton Ajouter ── */}
        <div className="pwd-header anim-fade-up">
          <div className="pwd-tabs">

            {/* Onglet PERSONAL — affiché uniquement si CREDENTIAL_VIEW_PERSONAL */}
            {canViewPersonal && (
              <button
                className={`pwd-tab${activeTab === "personal" ? " active" : ""}`}
                onClick={() => setActiveTab("personal")}
              >
                <i className="bi bi-person-fill me-2"/>
                Mes mots de passe
                <span className="pwd-tab-count">{personalCreds.length}</span>
              </button>
            )}

            {/* Onglet COMPANY — affiché uniquement si CREDENTIAL_VIEW_COMPANY */}
            {canViewCompany && (
              <button
                className={`pwd-tab${activeTab === "company" ? " active" : ""}`}
                onClick={() => setActiveTab("company")}
              >
                <i className="bi bi-building me-2"/>
                Entreprise
                <span className="pwd-tab-count">{companyCreds.length}</span>
              </button>
            )}
          </div>

          {/* Boutons Ajouter + Générateur */}
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <button className="pwd-gen-btn" onClick={() => setShowGenerator(true)}>
              <i className="bi bi-magic"/><span>Générateur</span>
            </button>
            {activeTab === "personal" && canEditPersonal && (
              <button className="pwd-add-btn" onClick={() => setShowModal(true)}>
                <i className="bi bi-plus-lg"/><span>Ajouter</span>
              </button>
            )}
          </div>
        </div>

        {/* ── Chargement ── */}
        {loading && (
          <div className="pwd-empty">
            <i className="bi bi-arrow-repeat spin" style={{ fontSize: 32, opacity: .4 }}/>
            <p>Chargement…</p>
          </div>
        )}

        {/* ── Erreur ── */}
        {!loading && error && (
          <div className="pwd-empty">
            <i className="bi bi-exclamation-triangle" style={{ color: "var(--danger)", opacity: 1 }}/>
            <p style={{ color: "var(--danger)" }}>{error}</p>
            <button className="pwd-add-btn" style={{ marginTop: 10 }} onClick={loadAll}>
              Réessayer
            </button>
          </div>
        )}

        {/* ── Contenu ── */}
        {!loading && !error && (
          <>
            {/* ── Onglet PERSONAL ──
                onEdit et onDelete ne sont passés que si CREDENTIAL_EDIT_PERSONAL.
                Si null → les boutons Modifier/Supprimer sont masqués dans CredRow. */}
            {activeTab === "personal" && canViewPersonal && (
              <CredentialList
                credentials={personalCreds}
                onCopy={handleCopyPassword}
                canCopy={true}
                onEdit={canEditPersonal ? (c) => { setEditCred(c); setShowModal(true); } : null}
                onDelete={canEditPersonal ? handleDelete : null}
                onReload={loadAll}
              />
            )}

            {/* ── Onglet COMPANY — toujours lecture seule pour les clients ──
                onEdit et onDelete sont toujours null.
                onCopy n'est passé que si CREDENTIAL_COPY_COMPANY — sinon null = bouton masqué. */}
            {activeTab === "company" && canViewCompany && (
              <>
                <div className="pwd-info-banner anim-fade-up">
                  <i className="bi bi-info-circle-fill me-2"/>
                  {canCopyCompany
                    ? "Ces identifiants appartiennent à votre entreprise. Vous pouvez consulter et copier les mots de passe."
                    : "Ces identifiants appartiennent à votre entreprise. Consultation uniquement."
                  }
                </div>
                <CredentialList
                  credentials={companyCreds}
                  onCopy={canCopyCompany ? handleCopyPassword : null}
                  canCopy={canCopyCompany}
                  onEdit={null}
                  onDelete={null}
                />
              </>
            )}
          </>
        )}

        {/* ── Modale générateur de mot de passe ── */}
        {showGenerator && (
          <PasswordGeneratorModal
            onClose={() => setShowGenerator(false)}
          />
        )}

        {/* ── Modale création / édition ── */}
        {showModal && (
          <CredentialModal
            editData={editCred}
            defaultScope="PERSONAL"
            onExternalSave={handleSaved}
            onExternalClose={() => { setShowModal(false); setEditCred(null); }}
          />
        )}
      </div>
      <style>{CSS}</style>
    </>
  );
}

// ─────────────────────────────────────────────
//  STYLES
// ─────────────────────────────────────────────

const CSS = `
/* En-tête : onglets + bouton Ajouter */
.pwd-header{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:16px;flex-wrap:wrap}

/* Onglets */
.pwd-tabs{display:flex;gap:4px;background:var(--navy-mid);border:1px solid var(--border);border-radius:10px;padding:4px}
.pwd-tab{display:flex;align-items:center;gap:6px;background:none;border:none;border-radius:7px;color:var(--text-2);font-size:13px;padding:7px 16px;cursor:pointer;transition:all .15s;white-space:nowrap}
.pwd-tab:hover{color:var(--text-1);background:rgba(255,255,255,.04)}
.pwd-tab.active{background:var(--accent-dim);color:var(--accent);font-weight:600}
.pwd-tab-count{background:rgba(255,255,255,.1);border-radius:20px;font-size:11px;font-weight:600;padding:1px 7px;margin-left:2px}
.pwd-tab.active .pwd-tab-count{background:var(--accent);color:#fff}

/* Bouton Générateur */
.pwd-gen-btn{display:flex;align-items:center;gap:7px;background:rgba(61,107,107,.15);border:1px solid rgba(61,107,107,.4);border-radius:10px;color:#3D6B6B;font-size:13px;font-weight:600;padding:9px 18px;cursor:pointer;flex-shrink:0;transition:all .18s}
.pwd-gen-btn:hover{background:rgba(61,107,107,.25);border-color:#3D6B6B}

/* Bouton Ajouter */
.pwd-add-btn{display:flex;align-items:center;gap:7px;background:var(--accent);border:none;border-radius:10px;color:#fff;font-size:13px;font-weight:600;padding:9px 18px;cursor:pointer;flex-shrink:0;transition:filter .18s;box-shadow:0 4px 14px rgba(232,65,47,.25)}
.pwd-add-btn:hover{filter:brightness(1.15)}

/* Bandeau lecture seule (onglet Entreprise) */
.pwd-info-banner{display:flex;align-items:center;background:rgba(0,119,182,.1);border:1px solid rgba(0,119,182,.25);border-radius:10px;color:#4DABF7;font-size:13px;padding:10px 16px;margin-bottom:12px}

/* Barre de filtres */
.pwd-toolbar{display:flex;align-items:center;gap:12px;margin-bottom:16px;flex-wrap:wrap}
.pwd-filters{display:flex;align-items:center;gap:4px;flex:1;flex-wrap:wrap}
.pwd-filter-btn{display:flex;align-items:center;gap:6px;background:var(--navy-mid);border:1px solid var(--border);border-radius:8px;color:var(--text-2);font-size:12px;padding:7px 12px;cursor:pointer;transition:all .15s;white-space:nowrap}
.pwd-filter-btn:hover{border-color:var(--accent);color:var(--accent);background:var(--accent-dim)}
.pwd-filter-btn.active{background:var(--accent-dim);border-color:var(--accent);color:var(--accent);font-weight:600}
.pwd-right{display:flex;align-items:center;gap:10px;margin-left:auto}
.pwd-count{font-size:11px;color:var(--text-3);white-space:nowrap}
.pwd-search-wrap{position:relative;display:flex;align-items:center}
.pwd-search-icon{position:absolute;left:10px;color:var(--text-3);font-size:13px;pointer-events:none}
.pwd-search{background:var(--navy-mid);border:1px solid var(--border);border-radius:8px;color:var(--text-1);font-size:13px;padding:7px 32px;width:200px;outline:none;transition:border-color .15s,width .25s}
.pwd-search::placeholder{color:var(--text-3)}
.pwd-search:focus{border-color:var(--accent);width:240px}
.pwd-search-clear{position:absolute;right:8px;background:none;border:none;color:var(--text-3);cursor:pointer;font-size:14px;padding:0}

/* Séparateur alphabétique */
.pwd-letter-sep{padding:6px 18px 5px;font-size:11px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:var(--text-3);background:rgba(255,255,255,.02);border-bottom:1px solid var(--border)}

/* Ligne credential */
.cred-row{display:flex;align-items:center;gap:14px;padding:10px 18px;transition:background .15s;cursor:pointer}
.cred-row:hover{background:rgba(255,255,255,.03)}
.cred-thumb{width:56px;height:34px;border-radius:7px;overflow:hidden;flex-shrink:0;line-height:0;box-shadow:0 2px 8px rgba(0,0,0,.3)}
.cred-thumb svg{display:block;width:100%;height:100%}
.cred-info{flex:0 1 260px;min-width:0}
.cred-name{display:block;font-size:14px;font-weight:600;color:var(--text-1);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cred-login{display:block;font-size:11.5px;color:var(--text-3);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:1px}
.cred-badges{display:flex;align-items:center;gap:6px;flex-shrink:0;margin-left:auto}
.cred-type-badge{font-size:10px;font-weight:500;padding:2px 8px;border-radius:20px;border:1px solid}
.cred-readonly-badge{font-size:10px;font-weight:500;padding:2px 8px;border-radius:20px;border:1px solid rgba(255,255,255,.15);color:var(--text-3);background:rgba(255,255,255,.05)}

/* Boutons d'action */
.cred-actions{display:flex;align-items:center;gap:4px;flex-shrink:0}
.cred-action-btn{width:30px;height:30px;border-radius:7px;background:none;border:1px solid transparent;color:var(--text-3);font-size:14px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .15s}
.cred-action-btn:hover{background:var(--border);color:var(--text-1)}

/* Menu déroulant */
.cred-menu-wrap{position:relative}
.cred-dropdown{position:absolute;right:0;top:calc(100% + 4px);z-index:9999;background:var(--navy-mid);border:1px solid var(--border);border-radius:10px;min-width:190px;box-shadow:0 12px 36px rgba(0,0,0,.4);overflow:hidden;animation:ddIn .12s ease}
@keyframes ddIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}}
.cred-dd-item{display:flex;align-items:center;width:100%;padding:9px 14px;background:none;border:none;color:var(--text-2);font-size:13px;cursor:pointer;transition:background .12s;text-align:left}
.cred-dd-item:hover{background:rgba(255,255,255,.05);color:var(--text-1)}
.cred-dd-item.danger{color:var(--danger)}
.cred-dd-item.danger:hover{background:var(--danger-dim)}
.cred-dd-sep{height:1px;background:var(--border);margin:4px 0}

/* Toast copie */
.pwd-copy-toast{background:rgba(105,219,124,.15);border-left:3px solid #69DB7C;color:#69DB7C;font-size:12px;padding:5px 14px}

/* État vide */
.pwd-empty{display:flex;flex-direction:column;align-items:center;padding:60px 20px;color:var(--text-3);text-align:center}
.pwd-empty i{font-size:42px;margin-bottom:14px;opacity:.4}
.pwd-empty p{font-size:14px;font-weight:500;color:var(--text-2);margin:0 0 4px}
.pwd-empty small{font-size:12px}

/* ── MODALE DE VISUALISATION ── */
.vw-overlay{position:fixed;inset:0;z-index:1055;background:rgba(10,12,22,.75);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;padding:16px;animation:vwFadeIn .15s ease}
@keyframes vwFadeIn{from{opacity:0}to{opacity:1}}
.vw-modal{background:var(--navy-mid,#1C1B1A);border:1px solid rgba(255,255,255,.09);border-radius:18px;box-shadow:0 40px 120px rgba(0,0,0,.6);width:100%;max-width:460px;max-height:90vh;display:flex;flex-direction:column;overflow:hidden;position:relative;animation:vwSlideIn .22s cubic-bezier(.34,1.56,.64,1)}
@keyframes vwSlideIn{from{transform:translateY(28px);opacity:0}to{transform:none;opacity:1}}
.vw-close{position:absolute;top:12px;right:12px;z-index:10;background:rgba(0,0,0,.4);border:none;cursor:pointer;color:rgba(255,255,255,.8);width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;transition:all .15s}
.vw-close:hover{background:rgba(0,0,0,.6);color:#fff}

/* Bannière colorée */
.vw-banner{width:100%;height:100px;flex-shrink:0;display:flex;align-items:center;justify-content:center;gap:14px;padding:0 24px}
.vw-banner-text{display:flex;flex-direction:column}
.vw-banner-name{font-size:1.1rem;font-weight:700;color:#fff}
.vw-banner-type{font-size:11px;color:rgba(255,255,255,.7);margin-top:2px}

/* Corps */
.vw-body{padding:18px 20px 22px;overflow-y:auto;flex:1}

/* Boutons Modifier/Supprimer */
.vw-action-row{display:flex;align-items:center;gap:8px;margin-bottom:16px}
.vw-btn-edit{display:flex;align-items:center;gap:6px;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.12);border-radius:8px;color:var(--text-2);font-size:12px;padding:6px 12px;cursor:pointer;transition:all .15s}
.vw-btn-edit:hover{background:rgba(255,255,255,.12);color:var(--text-1)}
.vw-delete-confirm{display:flex;align-items:center;gap:8px;background:rgba(239,68,68,.1);border:1px solid rgba(239,68,68,.25);border-radius:8px;padding:6px 12px;font-size:12px;color:var(--danger)}
.vw-confirm-yes{background:var(--danger-solid);border:none;border-radius:6px;color:#fff;font-size:11px;font-weight:600;padding:3px 10px;cursor:pointer}
.vw-confirm-no{background:rgba(255,255,255,.08);border:none;border-radius:6px;color:var(--text-2);font-size:11px;padding:3px 10px;cursor:pointer}

/* Champs */
.vw-fields{display:flex;flex-direction:column;gap:12px}
.vw-field{display:flex;flex-direction:column;gap:5px;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;padding:10px 14px}
.vw-field-label{font-size:10px;font-weight:600;color:var(--text-3);text-transform:uppercase;letter-spacing:.08em}
.vw-field-value-row{display:flex;align-items:center;gap:8px}
.vw-field-value{flex:1;font-size:13px;color:var(--text-1);word-break:break-all;line-height:1.5}
.vw-field-link{flex:1;font-size:13px;color:var(--accent);text-decoration:none;word-break:break-all}
.vw-field-link:hover{text-decoration:underline}
.vw-notes{white-space:pre-wrap;color:var(--text-2)}

/* Bouton icône (œil, copier) */
.vw-icon-btn{width:28px;height:28px;border-radius:7px;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);color:var(--text-3);font-size:13px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .15s;flex-shrink:0}
.vw-icon-btn:hover{background:rgba(255,255,255,.12);color:var(--text-1)}
.vw-icon-btn:disabled{opacity:.4;cursor:not-allowed}

/* Dates */
.vw-dates{display:flex;gap:14px;flex-wrap:wrap;padding-top:10px;border-top:1px solid rgba(255,255,255,.06);margin-top:4px}
.vw-dates span{font-size:11px;color:var(--text-3);display:flex;align-items:center;gap:4px}

/* Spinner */
@keyframes vwSpin{to{transform:rotate(360deg)}}
.vw-spin{animation:vwSpin .7s linear infinite;display:inline-block}
@keyframes spin{to{transform:rotate(360deg)}}
.spin{animation:spin .7s linear infinite;display:inline-block}`;